import { Controller, Control, FieldErrors } from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getErrorMessage } from '@/components/formErrorMessage';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
}

export const LoanStatusSection = ({ control, errors }: Props) => (
  <div className="p-8 mb-8" data-test-id="loan-status-container">
    <h3 className="section-title" data-test-id="loan-status-section">
      Loan Status
    </h3>
    <div className="grid-3" data-test-id="loan-status-grid">
      <div data-test-id="status-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_status.status"
          data-test-id="status-label"
        >
          Status
        </Label>
        <Controller
          name="loan_status.status"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="status-select"
            >
              <SelectTrigger
                className="loanform-select-trigger loanform-select-bg loanform-input-border"
                error={getErrorMessage(errors, 'loan_status.status')}
                hint="Current status of the loan. Default for impaired loans; Performing otherwise"
                data-test-id="status-trigger"
              >
                <SelectValue
                  placeholder="Select status"
                  data-test-id="status-value"
                />
              </SelectTrigger>
              <SelectContent
                className="loanform-select-content loanform-select-bg loanform-border"
                data-test-id="status-content"
              >
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Performing"
                  data-test-id="status-option-performing"
                >
                  Performing
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="30–59 Days Delinquent"
                  data-test-id="status-option-30-59-days"
                >
                  30–59 Days Delinquent
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="60–89 Days Delinquent"
                  data-test-id="status-option-60-89-days"
                >
                  60–89 Days Delinquent
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="90+ Days Delinquent"
                  data-test-id="status-option-90-plus-days"
                >
                  90+ Days Delinquent
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Default"
                  data-test-id="status-option-default"
                >
                  Default
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Foreclosure"
                  data-test-id="status-option-foreclosure"
                >
                  Foreclosure
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="REO"
                  data-test-id="status-option-reo"
                >
                  REO
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Forbearance"
                  data-test-id="status-option-forbearance"
                >
                  Forbearance
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div className="md:col-span-2" data-test-id="commentary-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_status.commentary"
          data-test-id="commentary-label"
        >
          Status Commentary *
        </Label>
        <Controller
          name="loan_status.commentary"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              id="loan_status.commentary"
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="commentary-input"
            />
          )}
        />
      </div>
      <div data-test-id="internal-credit-rating-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_status.internal_credit_rating"
          data-test-id="internal-credit-rating-label"
        >
          Internal Credit Rating
        </Label>
        <Controller
          name="loan_status.internal_credit_rating"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="internal-credit-rating-select"
            >
              <SelectTrigger
                error={getErrorMessage(
                  errors,
                  'loan_status.internal_credit_rating'
                )}
                data-test-id="internal-credit-rating-trigger"
              >
                <SelectValue
                  placeholder="Select rating"
                  data-test-id="internal-credit-rating-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="internal-credit-rating-content">
                <SelectItem
                  value="AAA"
                  data-test-id="internal-credit-rating-option-aaa"
                >
                  AAA
                </SelectItem>
                <SelectItem
                  value="AA"
                  data-test-id="internal-credit-rating-option-aa"
                >
                  AA
                </SelectItem>
                <SelectItem
                  value="A"
                  data-test-id="internal-credit-rating-option-a"
                >
                  A
                </SelectItem>
                <SelectItem
                  value="BBB"
                  data-test-id="internal-credit-rating-option-bbb"
                >
                  BBB
                </SelectItem>
                <SelectItem
                  value="BB"
                  data-test-id="internal-credit-rating-option-bb"
                >
                  BB
                </SelectItem>
                <SelectItem
                  value="B"
                  data-test-id="internal-credit-rating-option-b"
                >
                  B
                </SelectItem>
                <SelectItem
                  value="CCC"
                  data-test-id="internal-credit-rating-option-ccc"
                >
                  CCC
                </SelectItem>
                <SelectItem
                  value="CC"
                  data-test-id="internal-credit-rating-option-cc"
                >
                  CC
                </SelectItem>
                <SelectItem
                  value="C"
                  data-test-id="internal-credit-rating-option-c"
                >
                  C
                </SelectItem>
                <SelectItem
                  value="D"
                  data-test-id="internal-credit-rating-option-d"
                >
                  D
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="watchlist-monitoring-field">
        <Label
          className="form-label loanform-label"
          htmlFor="loan_status.watchlist_monitoring"
          data-test-id="watchlist-monitoring-label"
        >
          Watchlist Monitoring
        </Label>
        <Controller
          name="loan_status.watchlist_monitoring"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="watchlist-monitoring-select"
            >
              <SelectTrigger
                error={getErrorMessage(
                  errors,
                  'loan_status.watchlist_monitoring'
                )}
                data-test-id="watchlist-monitoring-trigger"
              >
                <SelectValue
                  placeholder="Select"
                  data-test-id="watchlist-monitoring-value"
                />
              </SelectTrigger>
              <SelectContent data-test-id="watchlist-monitoring-content">
                <SelectItem
                  value="Y"
                  data-test-id="watchlist-monitoring-option-y"
                >
                  Y
                </SelectItem>
                <SelectItem
                  value="N"
                  data-test-id="watchlist-monitoring-option-n"
                >
                  N
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
    </div>
  </div>
);
